"use client";

import { useId, useState, type ReactElement } from "react";
import type React from "react";
import { useTheme } from "next-themes";
import {
  Toaster as Sonner,
  toast as sonnerToast,
  type ExternalToast,
  type ToasterProps,
} from "sonner";

import { cn as cx } from "../../lib/utils";
import { useMountEffect } from "../../hooks/use-mount-effect";

type ToastMessage = React.ReactNode | (() => React.ReactNode);
type ToastType =
  | "success"
  | "info"
  | "warning"
  | "error"
  | "loading"
  | "message";

declare global {
  var __shoToasterId: string | undefined;
}

const toastStyles = {
  reset: "border-0! bg-transparent! p-0! shadow-none! outline-none!",
  content: "m-0! block! p-0!",
  icon: "hidden!",
  shell: "dark w-fit overflow-visible rounded-xl bg-bg-page",
  card: "relative w-80 overflow-visible rounded-[10px] border border-border-primary bg-bg-elevated p-3 shadow-L4",
  row: "flex w-full items-start gap-2",
  successIcon:
    "shrink-0 pt-px leading-none text-text-green [&>svg]:size-4",
  text: "min-w-0 flex-1",
  title:
    "m-0 break-words text-nav font-medium leading-nav text-text-primary",
};

function CheckIcon(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM15.667 8.12695C15.3228 7.89661 14.8574 7.98889 14.627 8.33301L10.4482 14.5723L8.28125 12.3945C7.98911 12.1009 7.51434 12.0995 7.2207 12.3916C6.92733 12.6838 6.9267 13.1586 7.21875 13.4521L10.0312 16.2793C10.1897 16.4384 10.4112 16.5177 10.6348 16.4961C10.8584 16.4744 11.0605 16.3537 11.1855 16.167L15.873 9.16699C16.1034 8.82283 16.0111 8.35739 15.667 8.12695Z"
        fill="currentColor"
      />
    </svg>
  );
}

function withDedupe(
  message: ToastMessage,
  options?: ExternalToast,
): ExternalToast | undefined {
  if (options?.id != null) return options;
  if (typeof message !== "string" && typeof message !== "number")
    return options;
  return { ...options, id: `sho-toast:${String(message)}` };
}

function renderMessage(message: ToastMessage): React.ReactNode {
  return typeof message === "function" ? message() : message;
}

function toastContent(type: ToastType | undefined, message: ToastMessage) {
  return (
    <div className={toastStyles.shell}>
      <div className={toastStyles.card}>
        <div className={toastStyles.row}>
          {type === "success" ? (
            <div className={toastStyles.successIcon}>
              <CheckIcon />
            </div>
          ) : null}
          <div className={toastStyles.text}>
            <p className={toastStyles.title}>{renderMessage(message)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function showToast(
  type: ToastType,
  message: ToastMessage,
  options?: ExternalToast,
) {
  return sonnerToast(toastContent(type, message), withDedupe(message, options));
}

const toast = Object.assign(
  (message: ToastMessage, options?: ExternalToast) =>
    sonnerToast(toastContent(undefined, message), withDedupe(message, options)),
  {
    success: (message: ToastMessage, options?: ExternalToast) =>
      showToast("success", message, options),
    info: (message: ToastMessage, options?: ExternalToast) =>
      showToast("info", message, options),
    warning: (message: ToastMessage, options?: ExternalToast) =>
      showToast("warning", message, options),
    error: (message: ToastMessage, options?: ExternalToast) =>
      showToast("error", message, options),
    loading: (message: ToastMessage, options?: ExternalToast) =>
      showToast("loading", message, options),
    message: (message: ToastMessage, options?: ExternalToast) =>
      showToast("message", message, options),
    custom: sonnerToast.custom,
    promise: sonnerToast.promise as (typeof sonnerToast)["promise"],
    dismiss: sonnerToast.dismiss,
    getHistory: sonnerToast.getHistory,
    getToasts: sonnerToast.getToasts,
  },
);

function Toaster({ ...props }: ToasterProps) {
  const theme = (useTheme().theme ?? "system") as NonNullable<
    ToasterProps["theme"]
  >;
  const id = useId();
  const [mounted, setMounted] = useState(false);

  useMountEffect(() => {
    if (globalThis.__shoToasterId == null) {
      globalThis.__shoToasterId = id;
      setMounted(true);
    }

    return () => {
      if (globalThis.__shoToasterId === id)
        globalThis.__shoToasterId = undefined;
    };
  });

  if (!mounted) return null;

  return (
    <Sonner
      theme={theme}
      closeButton={false}
      className={cx("toaster", "group")}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: toastStyles.reset,
          content: toastStyles.content,
          title: toastStyles.content,
          icon: toastStyles.icon,
        },
      }}
      {...props}
    />
  );
}

export { Toaster, toast };
